import type { ReactNode } from 'react';

export interface StatProps {
  label: string;
  /**
   * Der Wert. `null` bedeutet ausdruecklich "nicht ermittelbar" und wird als
   * Gedankenstrich gezeigt — niemals als 0.
   */
  value: number | string | null;
  /** Einheit hinter dem Wert, etwa % oder CHF. */
  unit?: string;
  /** Einordnung darunter, etwa "von 1 240 versendeten". */
  hint?: ReactNode;
  /** Info-Knopf neben der Beschriftung. */
  info?: ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}

/**
 * Kennzahl.
 *
 * Setzt Grundsatz 1 des Projekts durch: eine Rate ohne Grundmenge ist `null`
 * und wird als „—" gezeigt, nicht als „0 %". Der Unterschied ist nicht
 * kosmetisch — „0 % Oeffnungsrate" heisst, niemand hat geoeffnet; „—" heisst,
 * es wurde noch nichts versendet. Wer das verwechselt, sucht einen Fehler,
 * den es nicht gibt, oder uebersieht einen, den es gibt.
 *
 * Deshalb nimmt die Komponente `null` entgegen, statt dem Aufrufer die
 * Entscheidung zu ueberlassen.
 */
export function Stat({ label, value, unit, hint, info, tone = 'default' }: StatProps) {
  const fehlt = value === null;
  const anzeige =
    fehlt ? '—' : typeof value === 'number' ? value.toLocaleString('de-CH') : value;

  return (
    <div className="stat" data-tone={tone === 'default' ? undefined : tone}>
      <div className="stat__label">
        <span>{label}</span>
        {info}
      </div>
      <p className="stat__value" data-empty={fehlt || undefined}>
        {anzeige}
        {!fehlt && unit && <span className="stat__unit">{unit}</span>}
      </p>
      {/* Bei fehlendem Wert ersetzt die Begruendung den Hinweis — sonst
          steht dort ein Gedankenstrich ohne jede Erklaerung. */}
      <p className="stat__hint">{fehlt ? 'Noch keine Grundlage für diese Zahl.' : hint}</p>
    </div>
  );
}
